if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );

          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            // ─────────────────────────────────────────────────────────────
            // ERROR / 422
            // ─────────────────────────────────────────────────────────────
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              /*
               * Shopify-Sonderfall:
               *
               * Beispiel:
               * 3 Stück angefordert
               * nur 2 Stück verfügbar
               *
               * Shopify fügt 2 Stück tatsächlich zum Warenkorb hinzu,
               * antwortet aber trotzdem mit HTTP-/Cart-Status 422.
               *
               * Dawn behandelt das normalerweise nur als Fehler und
               * aktualisiert deshalb cart-notification / cart-count-bubble
               * nicht.
               */
              if (this.isPartialInventoryAdd(response) && this.cart) {
                try {
                  const refreshed = await this.refreshCartAfterPartialAdd(
                    formData.get('id')
                  );

                  if (refreshed) {
                    this.error = false;
                    return;
                  }
                } catch (error) {
                  console.error(
                    '[product-form] Cart refresh after partial inventory add failed:',
                    error
                  );
                }
              }

              // Normaler Fehler:
              // z.B. wirklich ausverkauft / nichts hinzugefügt
              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) {
                this.error = true;
                return;
              }

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');

              this.error = true;
              return;
            }

            // ─────────────────────────────────────────────────────────────
            // NORMALER SUCCESS
            // ─────────────────────────────────────────────────────────────
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(response);
                }
              );
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      /*
       * Erkennt den Shopify-Sonderfall:
       * Ein Teil der gewünschten Menge wurde trotz 422 hinzugefügt.
       */
      isPartialInventoryAdd(response) {
        if (response.status !== 422) return false;

        const message = `${response.message || ''} ${
          response.description || ''
        }`.toLowerCase();

        return (
          // Deutsche Shopify-Meldung
          (
            message.includes('aufgrund der verfügbarkeit') &&
            message.includes('hinzugefügt')
          ) ||

          // Zusätzlicher deutscher Fallback
          (
            message.includes('nur') &&
            message.includes('artikel') &&
            message.includes('hinzugefügt')
          ) ||

          // Englische Shopify-Meldung
          (
            message.includes('only') &&
            message.includes('added to your cart')
          )
        );
      }

      /*
       * Lädt nach einem Partial-Add den echten Warenkorb neu
       * und holt die benötigten Cart-Sections erneut von Shopify.
       *
       * Dadurch werden:
       * - cart-count-bubble aktualisiert
       * - cart-notification aktualisiert
       * - cart-notification geöffnet
       */
      async refreshCartAfterPartialAdd(variantId) {
        // ── 1. Tatsächlichen Warenkorb laden ──────────────────────────────
        const cartResponse = await fetch(`${routes.cart_url}.js`, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!cartResponse.ok) {
          throw new Error('Cart konnte nicht neu geladen werden.');
        }

        const cartData = await cartResponse.json();

        // Die betroffene Variante im Warenkorb suchen
        const cartItem = cartData.items.find(
          (item) => String(item.variant_id) === String(variantId)
        );

        if (!cartItem) {
          console.warn(
            '[product-form] Partial add gemeldet, Variante aber nicht im Cart gefunden.'
          );

          return false;
        }

        // ── 2. Benötigte Cart-Sections ermitteln ─────────────────────────
        const sectionIds = this.cart
          .getSectionsToRender()
          .map((section) => section.id);

        if (!sectionIds.length) {
          console.warn(
            '[product-form] Keine Cart-Sections zum Aktualisieren gefunden.'
          );

          return false;
        }

        // ── 3. Sections frisch von Shopify laden ─────────────────────────
        const separator = window.location.pathname.includes('?') ? '&' : '?';

        const sectionsUrl =
          `${window.location.pathname}` +
          `${separator}sections=${encodeURIComponent(sectionIds.join(','))}`;

        const sectionsResponse = await fetch(sectionsUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!sectionsResponse.ok) {
          throw new Error(
            'Cart-Sections konnten nicht neu geladen werden.'
          );
        }

        const sections = await sectionsResponse.json();

        // ── 4. Response-Struktur für Dawn nachbauen ──────────────────────
        const refreshedResponse = {
          id: cartItem.id,
          key: cartItem.key,
          sections: sections,
        };

        // ── 5. Cart Notification / Drawer + Bubble aktualisieren ─────────
        CartPerformance.measure(
          'add:paint-updated-sections',
          () => {
            this.cart.renderContents(refreshedResponse);
          }
        );

        // Optional auch andere Theme-Komponenten informieren
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: variantId,
          cartData: cartData,
        });

        return true;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}